import { Alert, Button, Group, Text, Code, Stack } from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getSafeMarkdownHref, isExternalMarkdownHref } from '@/utils/markdown.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import type { PaginatedResponse } from '@/types/common.ts';

export function SuiteError({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <Alert color="red" title="Unable to load or save">
      <Text size="sm">{getHttpErrorMessage(error)}</Text>
      {retry && (
        <Button variant="subtle" onClick={retry}>
          Retry
        </Button>
      )}
    </Alert>
  );
}

export function SuitePagination({
  data,
  onPage,
  disabled = false,
}: {
  data: Pick<PaginatedResponse<unknown>, 'next' | 'previous' | 'count'>;
  onPage: (page: number) => void;
  disabled?: boolean;
}) {
  return (
    <Group>
      <Button
        variant="default"
        disabled={disabled || !data.previous}
        onClick={() => data.previous && onPage(data.previous.page)}
      >
        Previous page
      </Button>
      <Text size="sm">{data.count} accessible results</Text>
      <Button
        variant="default"
        disabled={disabled || !data.next}
        onClick={() => data.next && onPage(data.next.page)}
      >
        Next page
      </Button>
    </Group>
  );
}

export function SuiteMarkdown({ body }: { body: string }) {
  return (
    <Stack gap="xs" style={{ overflowWrap: 'anywhere' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            const safe = getSafeMarkdownHref(href);
            return safe ? (
              <a
                href={safe}
                target={isExternalMarkdownHref(safe) ? '_blank' : undefined}
                rel={isExternalMarkdownHref(safe) ? 'noopener noreferrer' : undefined}
              >
                {children}
              </a>
            ) : (
              <span>{children}</span>
            );
          },
          img: ({ alt }) => (
            <Text size="sm" c="dimmed">
              Image reference: {alt || 'image'} (not loaded)
            </Text>
          ),
          pre: ({ children }) => (
            <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{children}</pre>
          ),
        }}
      >
        {body}
      </ReactMarkdown>
    </Stack>
  );
}

export function ExactVersion({ id, number }: { id: string | null; number: number | null }) {
  return id ? (
    <span>
      v{number ?? '?'} · <Code>{id}</Code>
    </span>
  ) : (
    <Text span c="dimmed">
      No version
    </Text>
  );
}
